import { FormProvider, useForm } from "react-hook-form";
import Form from "@/components/Form/Form";
import { FilledButton } from "@medoma/components/Button/Button";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  DateInput,
  type IDateInputFields,
} from "./AddActivityForm/Scheduling/DateInput";
import {
  TimeSlotsPicker,
  type ITimeSlotsPickerFields,
} from "./AddActivityForm/Scheduling/TimeSlotsPicker";
import { timeOfDaySchema, type ITimeOfDay } from "@medoma/models/activities";
import Checkbox from "@/components/Checkbox/Checkbox";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ANY_TIME_OF_DAY,
  rescheduleGroup,
  type IRescheduleGroup,
} from "@/api/Activities";
import { getTimespan } from "./AddActivityForm/activityTimeUtils";
import { deducedError, useErrorMessageAlert } from "@/Utils/ErrorUtils";
import { Spinner } from "@medoma/components/Spinner/Spinner";
import {
  calendarDateSchema,
  toPlainTimeInTimeZone,
} from "@medoma/models/date-and-time";
import { useUserProfile } from "@/api/Users";
import { Loading } from "@medoma/components/Loading/Loading";

type IEditGroupTimeFormFields = IDateInputFields &
  ITimeSlotsPickerFields & { editTime: string };

const EditGroupTimeForm = ({
  groupId,
  currentTimeCategory,
  groupStart,
  onSuccess,
}: {
  groupId: string;
  currentTimeCategory: ITimeOfDay;
  groupStart: Date;
  onSuccess: () => void;
}) => {
  const { t } = useLingui();
  const { timeZone } = useUserProfile();
  const methods = useForm<IEditGroupTimeFormFields>({
    defaultValues: {
      timeCategory: currentTimeCategory,
      recurrencesPerDay: "1", // Required to show a time slot input
    },
  });

  const { displayErrorMessageAlert } = useErrorMessageAlert();

  const queryClient = useQueryClient();
  const { mutate: reschedule, isPending } = useMutation({
    mutationFn: (data: IRescheduleGroup) => rescheduleGroup(groupId, data),
    onError: (error) => {
      const errorDisplayText = t`Det gick inte att byta tid på besöket`;
      displayErrorMessageAlert(`${errorDisplayText}. ${deducedError(error)}`);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      onSuccess();
    },
  });

  if (isPending) {
    return <Loading message={t`Flyttar besöket till ny tid`} />;
  }

  const { register, watch, handleSubmit } = methods;

  // Acceptable. We don't use react compiler, we just lint to find incompatibilities, such as this.
  // oxlint-disable-next-line react/incompatible-library
  const showTimeFields = watch("editTime");

  const validateAndSubmit = handleSubmit((formData) => {
    const { startDate, timeCategory, timeslots, timeSensitivity, editTime } =
      formData;

    // If we're not editing the time, we just want to update the date, and keep the existing time.
    if (!editTime) {
      const time =
        currentTimeCategory === timeOfDaySchema.enum.Any
          ? ANY_TIME_OF_DAY
          : toPlainTimeInTimeZone({ instant: groupStart, timeZone });

      reschedule({
        date: calendarDateSchema.decode(startDate),
        time,
      });

      return;
    }

    const isAnyTimeOfDay = timeCategory === timeOfDaySchema.enum.Any;
    const timespan = getTimespan({
      isAnyTimeOfDay,
      timeSensitivity,
    });

    const time = isAnyTimeOfDay
      ? ANY_TIME_OF_DAY
      : // @ts-expect-error we know that there is exactly one timeslot, but TS doesn't.
        timeslots[0].time; // We only have one timeslot if it's not recurring.

    reschedule({
      date: calendarDateSchema.decode(startDate),
      time,
      span: timespan,
    });
  });

  return (
    <FormProvider {...methods}>
      <Form onSubmit={validateAndSubmit}>
        <DateInput hasEndDate={false} />
        <Checkbox
          {...register("editTime")}
          label={{ text: t`Justera tiden` }}
        />
        {showTimeFields ? <TimeSlotsPicker isRecurring={false} /> : null}
        <FilledButton type="submit" disabled={isPending}>
          {isPending ? <Spinner /> : null}
          <Trans>Spara ändringar</Trans>
        </FilledButton>
      </Form>
    </FormProvider>
  );
};

export default EditGroupTimeForm;
